import {lazy, Suspense} from 'react';
import {AuthProvider, useAuth} from './contexts/AuthContext';
import {UserProvider, useUser} from './contexts/UserContext';
import {SessionProvider} from './contexts/SessionContext';
import './App.css';
import {
  Box,
  Heading,
  HStack,
  Link,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import Auth from './components/Auth';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import ViewLoader from './components/common/ViewLoader';
import PlannerPage from './components/planner/PlannerPage';
import AppShell from './components/AppShell';
import {APP_ROUTES, SIDEBAR_RESET_STATE_KEY} from './components/appShellRoutes';
import {
  ENCOUNTER_LIVE_ROUTE_PATTERN,
  GROUP_ENCOUNTER_ROUTE,
  PHONE_SESSION_ROUTE,
  TELEPHONIC_ENCOUNTER_ROUTE,
  VIDEO_SESSION_ROUTE,
} from './utils/encounterLaunch';

const TelephoneEncounterInterface = lazy(
  () => import('./components/TelephoneEncounterInterface'),
);
const PhoneEncounterThreeWayPage = lazy(
  () => import('./components/PhoneEncounterThreeWayPage'),
);
const PhoneSessionView = lazy(() => import('./components/PhoneSessionView'));
const VideoSessionView = lazy(() => import('./components/VideoSessionView'));
const TextTranslatorPage = lazy(
  () => import('./components/TextTranslatorPage'),
);
const EncounterInterface = lazy(
  () => import('./components/encounter/EncounterInterface'),
);
const SupportPage = lazy(() => import('./SupportPage'));
const RouteIndexPage = lazy(() => import('./components/RouteIndexPage'));
const ZoomDocs = lazy(() => import('./components/ZoomDocs'));
const TermsAndConditionsPage = lazy(
  () => import('./components/TermsAndConditionsPage'),
);
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const AccountDeletionPage = lazy(
  () => import('./components/AccountDeletionPage'),
);
const ApiDocumentationPage = lazy(
  () => import('./components/ApiDocumentationPage'),
);
const ApiToolsPage = lazy(() => import('./components/ApiToolsPage'));
const AgentsApiDocs = lazy(() => import('./components/AgentsApiDocs'));
const GroupEncounterPage = lazy(
  () => import('./components/GroupEncounterPage'),
);
const ScribePage = lazy(() => import('./components/scribe/ScribePage'));
const DocumentTranslatorPage = lazy(
  () => import('./components/DocumentTranslatorPage'),
);
const SuperAdminDashboard = lazy(
  () => import('./components/admin/SuperAdminDashboard'),
);
const EhrIntegrationManagement = lazy(
  () => import('./components/admin/EhrIntegrationManagement'),
);
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const PrivacyRightRequestsPage = lazy(
  () => import('./components/admin/PrivacyRightRequestsPage'),
);
const EmailEngineCRM = lazy(() => import('./components/admin/EmailEngineCRM'));
const BillingPage = lazy(() => import('./components/admin/BillingPanel'));
const LiveKitRecordingsPage = lazy(
  () => import('./components/admin/LiveKitRecordingsPage'),
);
const NoteInsightsDashboard = lazy(
  () => import('./components/admin/NoteInsightsDashboard'),
);
const PrivacyRequestSubmissionPage = lazy(
  () => import('./components/PrivacyRequestSubmissionPage'),
);
const ProcessPage = lazy(() => import('./components/session/ProcessPage'));
const InvitePage = lazy(() => import('./components/InvitePage'));
const PlannerToolsPage = lazy(
  () => import('./components/planner/PlannerToolsPage'),
);
const ChartingPage = lazy(() => import('./components/charting/ChartingPage'));
const TourPage = lazy(() => import('./components/tour/TourPage'));
const UniversityPage = lazy(
  () => import('./components/university/UniversityPage'),
);
const StartPage = lazy(() => import('./components/university/StartPage'));

const RouteSuspense = ({children}: Record<string, any>) => (
  <Suspense fallback={<ViewLoader label="Loading view…" />}>
    {children}
  </Suspense>
);

// Gates all routes behind userData — while the mandatory facility modal
// is visible (userData === null, loading === false) nothing renders
// underneath, matching the iOS fullScreenCover behaviour.
// Restricts a route to admin / super-admin accounts. Non-admins are bounced
// back to the hub. Must render inside UserProvider so useUser() is available.
const RequireAdmin = ({children}: Record<string, any>) => {
  const {userData} = useUser();
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  if (!isAdmin) {
    return <Navigate to={APP_ROUTES.home} replace />;
  }
  return children;
};

// Restricts a route to a set of roles. Anyone without a matching role is
// silently sent back to the hub (replace, so the blocked URL doesn't linger
// in history). This is the seamless alternative to a per-page "Access Denied"
// toast: an under-privileged account — e.g. a freshly created regular member
// who follows a stale admin link — just lands on their dashboard instead of a
// red error. Pairs with the page-level access gates as defense-in-depth.
const RequireRole = ({allow, children}: Record<string, any>) => {
  const {userData} = useUser();
  const role = userData?.role;
  if (!role || !allow.includes(role)) {
    return <Navigate to={APP_ROUTES.home} replace />;
  }
  return children;
};

const appShellElement = (children: any, scrollContent = false) => (
  <AppShell scrollContent={scrollContent}>{children}</AppShell>
);

const LegacyAdminDashboardRedirect = () => {
  const location = useLocation();
  return (
    <Navigate to={`${APP_ROUTES.admin}${location.search || ''}`} replace />
  );
};

const LiveEncounterRoute = ({currentUser}: Record<string, any>) => {
  return appShellElement(<EncounterInterface currentUser={currentUser} />);
};

export const AuthenticatedRoutes = ({
  logout,
  currentUser,
}: Record<string, any>) => {
  const {userData, loading} = useUser();
  const location = useLocation();
  const sidebarResetKey =
    location.state?.[SIDEBAR_RESET_STATE_KEY] || 'default';
  const routeResetKey = (routeName: string) =>
    `${routeName}-${sidebarResetKey}`;

  if (loading) {
    return <ViewLoader label="Getting things ready…" />;
  }

  if (!userData) {
    return null;
  }

  return (
    <>
      <RouteSuspense>
        <Routes>
          <Route path={APP_ROUTES.tour} element={<TourPage />} />
          <Route
            path={TELEPHONIC_ENCOUNTER_ROUTE}
            element={
              <TelephoneEncounterInterface
                logout={logout}
                currentUser={currentUser}
              />
            }
          />
          <Route
            path="/phone-conference"
            element={
              <PhoneEncounterThreeWayPage
                logout={logout}
                currentUser={currentUser}
              />
            }
          />
          <Route
            path={APP_ROUTES.textTranslator}
            element={appShellElement(<TextTranslatorPage />)}
          />
          <Route
            path={ENCOUNTER_LIVE_ROUTE_PATTERN}
            element={<LiveEncounterRoute currentUser={currentUser} />}
          />
          <Route
            path={PHONE_SESSION_ROUTE}
            element={appShellElement(
              <PhoneSessionView currentUser={currentUser} />,
            )}
          />
          <Route
            path={VIDEO_SESSION_ROUTE}
            element={appShellElement(
              <VideoSessionView currentUser={currentUser} />,
            )}
          />
          <Route
            path={GROUP_ENCOUNTER_ROUTE}
            element={appShellElement(<GroupEncounterPage />)}
          />
          <Route path="/scribe" element={appShellElement(<ScribePage />)} />
          <Route
            path={APP_ROUTES.documentTranslator}
            element={appShellElement(<DocumentTranslatorPage />)}
          />
          <Route
            path="/api-portal"
            element={<Navigate to="/api-documentation" replace />}
          />
          <Route
            path="/api-documentation"
            element={
              <RequireAdmin>
                <ApiDocumentationPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/api-tools"
            element={
              <RequireAdmin>
                <ApiToolsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/api-documentation/agents"
            element={
              <RequireAdmin>
                <AgentsApiDocs />
              </RequireAdmin>
            }
          />
          <Route
            path="/super-admin"
            element={
              <RequireRole allow={['superadmin']}>
                <SuperAdminDashboard key={routeResetKey('super-admin')} />
              </RequireRole>
            }
          />
          <Route
            path="/super-admin/billing"
            element={
              <RequireRole allow={['superadmin']}>
                <BillingPage />
              </RequireRole>
            }
          />
          <Route
            path="/super-admin/ehr-integration"
            element={
              <RequireRole allow={['superadmin']}>
                <EhrIntegrationManagement />
              </RequireRole>
            }
          />
          <Route
            path="/super-admin/note-insights"
            element={
              <RequireRole allow={['superadmin']}>
                <NoteInsightsDashboard />
              </RequireRole>
            }
          />
          <Route
            path="/super-admin/recordings"
            element={
              <RequireRole allow={['superadmin']}>
                <LiveKitRecordingsPage />
              </RequireRole>
            }
          />
          <Route
            path="/super-admin/recordings/:egressId"
            element={
              <RequireRole allow={['superadmin']}>
                <LiveKitRecordingsPage />
              </RequireRole>
            }
          />
          <Route
            path={APP_ROUTES.admin}
            element={
              <RequireRole allow={['admin', 'superadmin']}>
                {appShellElement(
                  <AdminDashboard key={routeResetKey('admin')} />,
                )}
              </RequireRole>
            }
          />
          <Route
            path="/admin-dashboard"
            element={<LegacyAdminDashboardRedirect />}
          />
          <Route
            path="/privacy-requests"
            element={
              <RequireRole allow={['superadmin']}>
                <PrivacyRightRequestsPage />
              </RequireRole>
            }
          />
          <Route
            path="/email-engine"
            element={
              <RequireRole allow={['superadmin']}>
                <EmailEngineCRM />
              </RequireRole>
            }
          />
          <Route
            path="/privacy-request"
            element={<PrivacyRequestSubmissionPage />}
          />
          <Route
            path={APP_ROUTES.process}
            element={appShellElement(<ProcessPage />)}
          />
          <Route
            path={APP_ROUTES.tools}
            element={appShellElement(
              <PlannerToolsPage key={routeResetKey('tools')} />,
            )}
          />
          <Route
            path={APP_ROUTES.charting}
            element={appShellElement(
              <ChartingPage key={routeResetKey('charting')} />,
            )}
          />
          <Route
            path={APP_ROUTES.learn}
            element={appShellElement(
              <UniversityPage key={routeResetKey('learn')} />,
            )}
          />
          <Route
            path={APP_ROUTES.home}
            element={appShellElement(
              <PlannerPage key={routeResetKey('home')} />,
            )}
          />
        </Routes>
      </RouteSuspense>
      <SessionTimeoutWarning />
    </>
  );
};

// Separate component for the authenticated content
const AuthenticatedApp = () => {
  const {currentUser, logout} = useAuth();

  return (
    <SessionProvider>
      <UserProvider>
        <AuthenticatedRoutes logout={logout} currentUser={currentUser} />
      </UserProvider>
    </SessionProvider>
  );
};

const LoginWrapper = () => {
  const location = useLocation();
  const {login, loginWithMicrosoft, resetPassword} = useAuth();

  // Pass the current path as state to the Auth component
  return (
    <Login
      login={login}
      loginWithMicrosoft={loginWithMicrosoft}
      resetPassword={resetPassword}
      intendedPath={`${location.pathname}${location.search}`}
    />
  );
};

const MotionBox = motion(Box);

// Reuses the fade ease curve from GeneralInstructions and the subtle vertical
// entrance from PlannerPage so signing in/out feels consistent with the rest
// of the app: the outgoing view drifts up and fades, the incoming one rises in.
const AUTH_VIEW_TRANSITION = {duration: 0.4, ease: [0.4, 0, 0.2, 1]};

const AUTH_VIEW_VARIANTS = {
  initial: {opacity: 0, y: 8},
  animate: {opacity: 1, y: 0},
  exit: {opacity: 0, y: -8},
};

// Crossfades between the login screen and the authenticated app. Since auth is
// a state flip (no route change), AnimatePresence "mode=wait" lets the current
// view animate out before the next animates in.
const AuthGate = () => {
  const {currentUser} = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const isAuthenticated = Boolean(currentUser);

  const motionProps = prefersReducedMotion
    ? {}
    : {
        variants: AUTH_VIEW_VARIANTS,
        initial: 'initial',
        animate: 'animate',
        exit: 'exit',
        transition: AUTH_VIEW_TRANSITION,
      };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <MotionBox
        key={isAuthenticated ? 'app' : 'login'}
        w="100%"
        {...motionProps}
      >
        {isAuthenticated ? <AuthenticatedApp /> : <LoginWrapper />}
      </MotionBox>
    </AnimatePresence>
  );
};

export const LandingPage = () => {
  return (
    <RouteSuspense>
      <Routes>
        <Route path="/support" element={<SupportPage />} />
        <Route path="/index" element={<RouteIndexPage />} />
        <Route path="/docs/zoom" element={<ZoomDocs />} />
        <Route
          path="/docs/legal/terms-and-conditions"
          element={<TermsAndConditionsPage />}
        />
        <Route
          path="/docs/legal/privacy-policy"
          element={<PrivacyPolicyPage />}
        />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/invite" element={<InvitePage />} />
        {/* Assisted sign-in: public by design so a rollout email can link
            straight to the "how to sign in" video before any credentials. */}
        <Route path="/start" element={<StartPage />} />
        <Route path="*" element={<AuthGate />} />
      </Routes>
    </RouteSuspense>
  );
};

const Login = ({
  login,
  loginWithMicrosoft,
  resetPassword,
  intendedPath,
}: Record<string, any>) => {
  return (
    <Box
      minH="100vh"
      overflowY="auto"
      overflowX="hidden"
      display="grid"
      gridTemplateColumns={{
        base: 'minmax(0, 1fr)',
        xl: 'minmax(0, 500px) minmax(0, 640px)',
      }}
      gridTemplateAreas={{
        base: '"login" "announcement"',
        xl: '"login announcement"',
      }}
      alignContent="center"
      alignItems="center"
      justifyItems={{base: 'center', xl: 'stretch'}}
      justifyContent="center"
      columnGap={{xl: 'clamp(4rem, 7vw, 8em)'}}
      rowGap={{base: 8, md: 10, xl: 0}}
      px={{base: 4, sm: 6, md: 8, xl: 6, '2xl': '8em'}}
      py={{base: 4, sm: 5, md: 2, xl: 4}}
      bg={useColorModeValue('gray.50', 'gray.900')}
    >
      <Box
        gridArea="login"
        maxW={{base: '420px', md: '500px'}}
        w="100%"
        minW={0}
        justifySelf={{base: 'center', xl: 'start'}}
        bg={useColorModeValue('white', 'whiteAlpha.50')}
        borderWidth="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        borderRadius="xl"
        boxShadow={useColorModeValue(
          '0 1px 2px rgba(23, 23, 23, 0.04), 0 12px 28px -14px rgba(23, 23, 23, 0.16)',
          '0 1px 2px rgba(0, 0, 0, 0.4)',
        )}
        overflow="visible"
        px={{base: 5, sm: 8, md: '4em'}}
        py={{base: 5, sm: 6, xl: '2em'}}
      >
        <Box w="100%">
          <HStack
            justify="center"
            spacing={{base: 3, sm: '20px'}}
            mb={{base: 6, sm: 8, xl: '4em'}}
          >
            <Box
              aria-hidden="true"
              h={{base: '36px', sm: '42px'}}
              w={{base: '36px', sm: '42px'}}
              bg="opalite.600"
              flexShrink={0}
              sx={{
                mask: "url('/Opalite_Health_logo_v1_tight.png') center / contain no-repeat",
                WebkitMask:
                  "url('/Opalite_Health_logo_v1_tight.png') center / contain no-repeat",
              }}
            />
            <Text
              color="opalite.600"
              fontSize={{base: '1.45rem', sm: '1.7rem'}}
              fontWeight={600}
              lineHeight="1.1"
            >
              Opalite Health
            </Text>
          </HStack>

          <Heading
            size={{base: 'lg', md: 'xl'}}
            color={useColorModeValue('gray.800', 'gray.100')}
            fontWeight="semibold"
            textAlign="center"
            mb={0}
            pt={0}
            pb={2}
          >
            Welcome
          </Heading>
          <Text
            color={useColorModeValue('gray.600', 'gray.300')}
            fontSize={{base: 'sm', md: 'md'}}
            textAlign="center"
            mb={4}
          >
            Sign in to continue
          </Text>
          <Auth
            login={login}
            resetPassword={resetPassword}
            loginWithMicrosoft={loginWithMicrosoft}
            intendedPath={intendedPath}
          />
        </Box>
      </Box>
      <Box
        gridArea="announcement"
        w="100%"
        maxW={{base: '500px', xl: '640px'}}
        minW={0}
        justifySelf={{base: 'center', xl: 'start'}}
        textAlign={{base: 'center', xl: 'left'}}
        aria-label="Product update"
      >
        <Text
          as="h2"
          color={useColorModeValue('gray.800', 'gray.100')}
          fontSize={{base: 'xl', sm: '2xl', md: '3xl', xl: '4xl', '2xl': '5xl'}}
          fontWeight="normal"
          lineHeight="1.15"
          mt={0}
          mb={4}
        >
          Opalite got a little glow-up.
        </Text>
        <Text
          color={useColorModeValue('gray.700', 'gray.200')}
          fontSize={{base: 'sm', xl: 'md'}}
          lineHeight="1.6"
        >
          We&apos;ve refreshed the experience to make Opalite easier to use,
          inspired by your feedback. Questions or suggestions? We&apos;d love to
          hear from you at{' '}
          <Link
            color={useColorModeValue('opalite.600', 'opalite.300')}
            fontWeight="semibold"
            href="mailto:support@opalitehealth.com"
          >
            support@opalitehealth.com
          </Link>
          .
        </Text>
        <Text
          color={useColorModeValue('gray.700', 'gray.200')}
          fontSize={{base: 'sm', xl: 'md'}}
          lineHeight="1.6"
          mt={3}
        >
          Thank you for being part of Opalite and helping shape what&apos;s
          next.
        </Text>
        <Text
          color={useColorModeValue('gray.700', 'gray.200')}
          fontFamily="'Caveat', 'Segoe Script', 'Apple Chancery', 'Lucida Handwriting', cursive"
          fontStyle="normal"
          fontSize={{base: '1.5rem', sm: '1.625rem', xl: '2rem'}}
          fontWeight="normal"
          lineHeight="1.2"
          letterSpacing="0"
          mt={3}
          whiteSpace={{base: 'normal', sm: 'nowrap'}}
        >
          Cathleen, Alex, and Josh
        </Text>
      </Box>
    </Box>
  );
};

// Main App component
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <LandingPage />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
